import React, { forwardRef, memo, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { CopilotContext } from '@Sage/ssm-web5';

import useEntityDetail from './hooks/useEntityDetail';

const EntityDetailContent = forwardRef(({ children, onScroll, bottomSpacing = 0 }, ref) => {
    const { hasTabs } = useEntityDetail();
    const { copilotBarProps } = useContext(CopilotContext);
    const {
        height: copilotBarHeight,
        isMinimized: isCopilotBarMinimized,
        isOpen: isCopilotBarOpen,
        padding: copilotBarPadding,
    } = copilotBarProps;

    const copilotBottomInset = useMemo(() => {
        if (!isCopilotBarOpen || isCopilotBarMinimized) {
            return 0;
        }
        return copilotBarPadding * 2 + copilotBarHeight;
    }, [copilotBarHeight, copilotBarPadding, isCopilotBarMinimized, isCopilotBarOpen]);

    const totalBottomInset = useMemo(() => {
        return copilotBottomInset + bottomSpacing;
    }, [bottomSpacing, copilotBottomInset]);

    const shouldApplyBottomInset = totalBottomInset > 0;

    // Real spacer node. `margin-bottom`/`padding-bottom` on the scroll container is unreliable
    // when children overflow (scrollHeight ignores it on Chromium/WebKit); a sibling div with
    // explicit height always extends scrollHeight, freeing widgets from the absolute footer.
    const spacerStyle = useMemo(
        () => ({ height: totalBottomInset, flex: '0 0 auto' }),
        [totalBottomInset],
    );

    return (
        <div
            className={classNames('fm-entity-detail__content', {
                'fm-entity-detail__content--withTabs': hasTabs,
            })}
            ref={ref}
            onScroll={onScroll}
        >
            <div className="fm-entity-detail__content__inner">
                {children}
                {shouldApplyBottomInset && (
                    <div
                        aria-hidden="true"
                        className="fm-entity-detail__content__bottom-spacer"
                        style={spacerStyle}
                    />
                )}
            </div>
        </div>
    );
});

export default memo(EntityDetailContent);
